import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const BlogPage = () => {
  const { i18n } = useTranslation();

  const blogPosts = [
    {
      id: 1,
      title: i18n.language === 'he' ? 'הסקייטבורד החשמלי הטוב ביותר בישראל 2024' : 'Best Electric Skateboard in Israel 2024',
      excerpt: i18n.language === 'he' 
        ? 'מדריך מקיף לבחירת הסקייטבורד החשמלי הטוב ביותר בישראל. דגשים על טווח, נוחות, שירות ואמינות.'
        : 'Comprehensive guide to choosing the best electric skateboard in Israel, with a focus on range, comfort, service, and reliability.',
      image: '/pictures/LOGO.png',
      date: '2024-12-19',
      slug: 'best-electric-skateboard-israel-2024',
      keywords: ['electric skateboard Israel', 'best electric skateboard', 'BlueBoard', 'FREEBORD']
    },
    {
      id: 2,
      title: i18n.language === 'he' ? 'איך לבחור סקייטבורד חשמלי בתל אביב' : 'How to Choose an Electric Skateboard in Tel Aviv',
      excerpt: i18n.language === 'he'
        ? 'טיפים לבחירת סקייטבורד חשמלי מתאים לתל אביב. התחשבות בתנאי הדרך, מרחקים, ואפשרויות אחסון.'
        : 'Tips for choosing the right electric skateboard for Tel Aviv. Considering road conditions, distances, and storage options.',
      image: '/pictures/LOGO.png',
      date: '2024-12-18',
      slug: 'choose-electric-skateboard-tel-aviv',
      keywords: ['electric skateboard Tel Aviv', 'electric skateboard shop', 'Tel Aviv transportation']
    },
    {
      id: 3,
      title: i18n.language === 'he' ? 'בטיחות בסקייטבורד חשמלי - מדריך מלא' : 'Electric Skateboard Safety - Complete Guide',
      excerpt: i18n.language === 'he'
        ? 'מדריך בטיחות מקיף לסקייטבורדים חשמליים. ציוד מגן, חוקים בישראל, וטיפים לנסיעה בטוחה.'
        : 'Comprehensive safety guide for electric skateboards. Protective gear, Israeli laws, and safe riding tips.',
      image: '/pictures/LOGO.png',
      date: '2024-12-17',
      slug: 'electric-skateboard-safety-guide',
      keywords: ['electric skateboard safety', 'skateboard helmet', 'protective gear']
    },
    {
      id: 4,
      title: i18n.language === 'he' ? 'תחזוקת סקייטבורד חשמלי - מה חשוב לדעת' : 'Electric Skateboard Maintenance - What Matters Most',
      excerpt: i18n.language === 'he'
        ? 'איך לשמור על הסקייטבורד החשמלי שלך לאורך זמן: בדיקות שוטפות, סוללה, גלגלים וניקיון.'
        : 'How to keep your electric skateboard in top condition with routine checks, battery care, wheel upkeep, and cleaning.',
      image: '/pictures/LOGO.png',
      date: '2024-12-16',
      slug: 'electric-skateboard-maintenance-guide',
      keywords: ['electric skateboard maintenance', 'esk8 care', 'BlueBoard Israel']
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "BlueBoard Blog - Electric Skateboards Israel",
    "description": "Expert guides and reviews for electric skateboards in Israel.",
    "url": "https://blueboard.bloodshop.org/blog",
    "blogPost": blogPosts.map(post => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.excerpt,
      "datePublished": post.date,
      "url": `https://blueboard.bloodshop.org/blog/${post.slug}`,
      "author": {
        "@type": "Organization",
        "name": "BlueBoard"
      }
    }))
  };

  return (
    <>
      <SEO
        title="Electric Skateboard Blog - Expert Guides & Reviews | BlueBoard Israel"
        description="Expert guides, reviews, and tips for electric skateboards in Israel."
        keywords="electric skateboard blog, electric skateboard Israel, BlueBoard review, skateboard safety, Tel Aviv electric skateboard"
        url="/blog"
        type="website"
        lang={i18n.language as 'en' | 'he'}
        structuredData={structuredData}
      />
      
      <div className="bg-white min-h-screen py-12" dir={i18n.language === 'he' ? 'rtl' : 'ltr'}>
        <div className="container mx-auto px-4">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-4">
              {i18n.language === 'he' ? 'בלוג סקייטבורדים חשמליים' : 'Electric Skateboard Blog'}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {i18n.language === 'he'
                ? 'מדריכים מומחים, סקירות, וטיפים לסקייטבורדים חשמליים בישראל'
                : 'Expert guides, reviews, and tips for electric skateboards in Israel'
              }
            </p>
          </div>

          {/* Blog Posts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post) => (
              <article key={post.id} className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img 
                  src={post.image} 
                  alt={post.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <div className="text-sm text-gray-500 mb-2">
                    {new Date(post.date).toLocaleDateString(i18n.language === 'he' ? 'he-IL' : 'en-US')}
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 mb-3">
                    {post.title}
                  </h2>
                  <p className="text-gray-600 mb-4">
                    {post.excerpt}
                  </p>
                  <Link 
                    to={`/blog/${post.slug}`}
                    className="text-blue-600 font-semibold hover:text-blue-800 transition"
                  >
                    {i18n.language === 'he' ? 'קרא עוד' : 'Read More'} →
                  </Link>
                </div>
              </article>
            ))}
          </div>

          {/* CTA Section */}
          <div className="text-center mt-12 bg-gray-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {i18n.language === 'he' ? 'מוכן לנסות?' : 'Ready to Try?'}
            </h2>
            <p className="text-xl text-gray-600 mb-6">
              {i18n.language === 'he'
                ? 'צור קשר עכשיו לנסיעת מבחן חינם'
                : 'Contact us now for a free test ride'
              }
            </p>
                         <Link 
               to="/service#always-here-for-you" 
               className="bg-blue-600 text-white px-8 py-3 rounded-lg text-lg font-bold hover:bg-blue-700 transition"
             >
               {i18n.language === 'he' ? 'צור קשר' : 'Contact Us'}
             </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogPage; 
